import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Printer, Download } from "lucide-react";

/**
 * GuideQR — carte imprimable A5 avec QR code élégant qui pointe
 * vers /guide. À laisser dans le refuge (sur la table, dans un
 * petit cadre) pour que les voyageurs accèdent au guide en un scan.
 *
 * L'esthétique reprend l'univers du carnet : papier crème, filet
 * doré, calligraphie. Bouton "Imprimer" avec CSS optimisée pour A5.
 */
export default function GuideQR() {
  const [guideUrl, setGuideUrl] = useState("");

  useEffect(() => {
    document.title = "QR — Guide du Voyageur · Curieux Refuge";
    // On construit dynamiquement l'URL absolue vers /guide
    if (typeof window !== "undefined") {
      setGuideUrl(`${window.location.origin}/guide`);
    }
  }, []);

  return (
    <div
      data-testid="guide-qr-page"
      className="min-h-screen"
      style={{
        background: "linear-gradient(180deg, #F2E8D9 0%, #FBF5EA 100%)",
      }}
    >
      {/* Barre supérieure — masquée à l'impression */}
      <div className="print:hidden w-full max-w-3xl mx-auto px-5 sm:px-8 pt-6 md:pt-8 flex items-center gap-4">
        <Link
          to="/guide"
          data-testid="qr-back"
          className="inline-flex items-center gap-2 font-body text-[11px] tracking-[0.2em] uppercase transition-colors"
          style={{ color: "var(--cr-ink-soft)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--cr-ruby)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--cr-ink-soft)")}
        >
          <ArrowLeft size={14} strokeWidth={1.4} />
          Retour au Guide
        </Link>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="qr-print-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-body text-[11px] tracking-[0.2em] uppercase transition-all"
            style={{
              background: "var(--cr-ruby)",
              color: "#FAF4EC",
            }}
          >
            <Printer size={14} strokeWidth={1.5} />
            Imprimer
          </button>
        </div>
      </div>

      <p className="print:hidden max-w-2xl mx-auto text-center mt-6 md:mt-8 px-5 font-display italic font-light" style={{ color: "var(--cr-ink-soft)", fontSize: "0.95rem" }}>
        Petite carte à imprimer et laisser dans le refuge — un scan et vos voyageurs entrent dans le guide.
      </p>

      {/* ===== Zone imprimable ===== */}
      <div className="w-full flex items-center justify-center px-4 py-10 md:py-14">
        <div
          data-testid="qr-print-card"
          className="cr-guide-open relative bg-white text-center"
          style={{
            width: "min(100%, 420px)",
            aspectRatio: "1 / 1.414", // proche du A5
            padding: "clamp(28px, 6%, 40px)",
            borderRadius: "8px",
            border: "1px solid rgba(184, 153, 104, 0.55)",
            background:
              "linear-gradient(180deg, #FDF9F0 0%, #FBF5EA 100%)",
            boxShadow: "0 24px 60px rgba(46, 31, 24, 0.12)",
          }}
        >
          {/* Cadre intérieur doré */}
          <div
            aria-hidden="true"
            className="absolute inset-3 pointer-events-none"
            style={{
              border: "1px solid rgba(184, 153, 104, 0.5)",
              borderRadius: "4px",
            }}
          />

          {/* En-tête */}
          <div className="relative pt-2">
            <p
              className="font-body text-[9px] tracking-[0.36em] uppercase"
              style={{ color: "var(--cr-ruby)" }}
            >
              Curieux Refuge
            </p>
            <div className="flex items-center justify-center gap-2 mt-3 mb-4">
              <span className="h-[1px] w-6" style={{ background: "var(--cr-gold)" }} />
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--cr-gold)" }}
              />
              <span className="h-[1px] w-6" style={{ background: "var(--cr-gold)" }} />
            </div>
            <h1
              className="font-display font-light leading-[1.05]"
              style={{
                color: "var(--cr-ink)",
                fontSize: "clamp(1.5rem, 4.5vw, 2rem)",
              }}
            >
              Le <span className="italic">Guide</span>
              <br />du Voyageur
            </h1>
            <p
              className="font-display italic font-light mt-2"
              style={{ color: "var(--cr-ink-soft)", fontSize: "0.85rem" }}
            >
              Votre carnet de séjour
            </p>
          </div>

          {/* QR Code */}
          <div className="relative flex items-center justify-center py-6 md:py-8">
            {guideUrl && (
              <div
                className="p-3 bg-white"
                style={{
                  borderRadius: "6px",
                  border: "1px solid rgba(184, 153, 104, 0.35)",
                }}
              >
                <QRCodeSVG
                  value={guideUrl}
                  size={180}
                  level="H"
                  fgColor="#2E1F18"
                  bgColor="#FFFFFF"
                  marginSize={0}
                />
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="relative">
            <p
              className="font-display font-light"
              style={{ color: "var(--cr-ink)", fontSize: "1rem" }}
            >
              Scannez pour ouvrir le guide
            </p>
            <p
              className="font-body text-[10px] tracking-[0.22em] uppercase mt-2"
              style={{ color: "var(--cr-ruby)" }}
            >
              Avant · Pendant · Après votre séjour
            </p>
          </div>

          {/* Ornementation basse */}
          <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-2 opacity-60">
            <span className="h-[1px] w-6" style={{ background: "var(--cr-gold)" }} />
            <span
              className="font-body text-[9px] tracking-[0.32em] uppercase"
              style={{ color: "var(--cr-ruby)" }}
            >
              Saint-Gilles-les-Bains
            </span>
            <span className="h-[1px] w-6" style={{ background: "var(--cr-gold)" }} />
          </div>
        </div>
      </div>

      {/* CSS spécifique pour l'impression */}
      <style>{`
        @media print {
          html, body { background: #FFFFFF !important; }
          [data-testid="qr-print-card"] {
            box-shadow: none !important;
            border: 1px solid rgba(184,153,104,0.55) !important;
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}
